import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Button } from '~/components/button';

export const Route = createFileRoute('/_authed/community/rooms/$id')({
  component: RoomView,
});

interface Message {
  messageId: string;
  senderPseudonym: string;
  isAi: boolean;
  text: string;
  createdAt: number;
}

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) throw new Error(`${path} -> ${res.status}: ${await res.text()}`);
  return (await res.json()) as T;
}

function RoomView() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement | null>(null);

  const messages = useQuery({
    queryKey: ['room-messages', id],
    queryFn: () => fetchJson<{ messages: Message[] }>(`/api/community/rooms/${id}/messages`),
    refetchInterval: 3000,
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.data?.messages.length]);

  const send = useMutation({
    mutationFn: (text: string) =>
      fetchJson<{ suspended?: boolean; route?: string }>(`/api/community/rooms/${id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      }),
    onSuccess: (r) => {
      if (r.suspended && r.route) {
        nav({ to: '/crisis-resources' });
        return;
      }
      setDraft('');
      qc.invalidateQueries({ queryKey: ['room-messages', id] });
    },
  });

  const flag = useMutation({
    mutationFn: (input: { messageId: string; reason: string }) =>
      fetchJson<{ id: string }>(`/api/community/rooms/${id}/flags`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });

  return (
    <section className="mx-auto max-w-2xl px-4 py-8 flex flex-col h-[calc(100vh-3.5rem)]">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold tracking-tight">room</h1>
        <Link to="/community/rooms" className="text-sm text-muted-foreground hover:text-foreground">
          ← rooms
        </Link>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        you appear to others under a stable pseudonym. only you can read your copy of this conversation.
      </p>

      <div className="flex-1 overflow-y-auto mt-4 space-y-3">
        {(messages.data?.messages ?? []).map((m) => (
          <div key={m.messageId + m.senderPseudonym} className="text-sm">
            <div className="text-xs text-muted-foreground flex items-baseline justify-between">
              <span>
                {m.senderPseudonym}
                {m.isAi && <span className="ml-1 italic">(facilitator)</span>}
              </span>
              <span>
                {new Date(m.createdAt).toLocaleTimeString()}{' '}
                <button
                  onClick={() => {
                    const reason = window.prompt('flag reason (optional)') ?? '';
                    flag.mutate({ messageId: m.messageId, reason });
                  }}
                  className="ml-2 underline text-[10px]"
                >
                  flag
                </button>
              </span>
            </div>
            <p className="mt-1 whitespace-pre-wrap leading-relaxed">{m.text}</p>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form
        className="mt-4 border-t border-border pt-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) send.mutate(draft.trim());
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="say something."
          className="w-full rounded-[var(--radius)] border border-input bg-background p-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          disabled={send.isPending}
        />
        <div className="mt-2 flex items-center gap-2">
          <Button type="submit" size="sm" disabled={send.isPending || !draft.trim()}>
            {send.isPending ? 'sending…' : 'send'}
          </Button>
          {flag.data && <span className="text-xs text-muted-foreground">flag #{flag.data.id.slice(0, 6)} sent.</span>}
        </div>
      </form>
    </section>
  );
}
